import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { referralService } from '@services';
import { useAuthStore } from '@app/stores/authStore';

export const PENDING_REWARDS_QUERY_KEY = [
  'referral',
  'pending-rewards',
] as const;
export const MY_REFERRER_QUERY_KEY = ['referral', 'my-referrer'] as const;

export const useMyReferrer = () => {
  const {
    data: referrer,
    isLoading,
    error,
  } = useQuery({
    queryKey: MY_REFERRER_QUERY_KEY,
    queryFn: () => referralService.getMyReferrer(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  return {
    referrer,
    isLoading,
    error,
  };
};

export const usePendingRewards = () => {
  const {
    data: pendingRewards = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: PENDING_REWARDS_QUERY_KEY,
    queryFn: () => referralService.getPendingRewards(),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });

  const totalPendingGold = pendingRewards.reduce(
    (sum, reward) => sum + reward.goldAmount,
    0
  );

  return {
    pendingRewards,
    totalPendingGold,
    hasPendingRewards: pendingRewards.length > 0,
    isLoading,
    error,
    refetch,
  };
};

export const useClaimRewards = () => {
  const queryClient = useQueryClient();

  const validateAndUpdateUser = useAuthStore(
    (state) => state.validateAndUpdateUser
  );

  const claimAllMutation = useMutation({
    mutationFn: () => referralService.claimAllRewards(),
    onSuccess: async () => {
      // Invalidate related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: PENDING_REWARDS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: MY_REFERRER_QUERY_KEY }),
      ]);
      // Refresh user profile
      await validateAndUpdateUser();
    },
  });

  const claimSingleMutation = useMutation({
    mutationFn: (rewardId: string) => referralService.claimReward(rewardId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: PENDING_REWARDS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: MY_REFERRER_QUERY_KEY }),
      ]);
      // Refresh user profile in Zustand store
      await validateAndUpdateUser();
    },
  });

  return {
    claimAll: claimAllMutation.mutateAsync,
    claimSingle: claimSingleMutation.mutateAsync,
    isClaimingAll: claimAllMutation.isPending,
    isClaimingSingle: claimSingleMutation.isPending,
    isClaiming: claimAllMutation.isPending || claimSingleMutation.isPending,
  };
};
